export type LibraryDocRef = {
  id: string;
  itemType: "note" | "reference";
};

export type LibraryRecentEntry = LibraryDocRef & {
  openedAt: number;
};

export const LIBRARY_RECENT_MAX = 10;

const RECENT_STORAGE_PREFIX = "library-recent-";
export const LIBRARY_RECENT_CHANGED_EVENT = "library-recent-changed";

/** Stable empty snapshot for useSyncExternalStore (must not allocate per read). */
const EMPTY_RECENT: LibraryRecentEntry[] = [];

type RecentSnapshotCache = {
  signature: string;
  snapshot: LibraryRecentEntry[];
};

const recentSnapshotByUser = new Map<string, RecentSnapshotCache>();

function recentSignature(entries: LibraryRecentEntry[]): string {
  return entries.map((e) => `${e.id}:${e.itemType}:${e.openedAt}`).join("|");
}

function parseRecentFromStorage(raw: string | null): LibraryRecentEntry[] {
  if (!raw) return EMPTY_RECENT;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return EMPTY_RECENT;
    const entries = parsed
      .filter(
        (e): e is LibraryRecentEntry =>
          e != null &&
          typeof e === "object" &&
          typeof (e as LibraryRecentEntry).id === "string" &&
          ((e as LibraryRecentEntry).itemType === "note" ||
            (e as LibraryRecentEntry).itemType === "reference") &&
          typeof (e as LibraryRecentEntry).openedAt === "number"
      )
      .slice(0, LIBRARY_RECENT_MAX);
    return entries.length === 0 ? EMPTY_RECENT : entries;
  } catch {
    return EMPTY_RECENT;
  }
}

function cacheRecentSnapshot(userId: string, entries: LibraryRecentEntry[]): LibraryRecentEntry[] {
  const signature = recentSignature(entries);
  const cached = recentSnapshotByUser.get(userId);
  if (cached?.signature === signature) {
    return cached.snapshot;
  }
  const snapshot = entries === EMPTY_RECENT ? EMPTY_RECENT : [...entries];
  recentSnapshotByUser.set(userId, { signature, snapshot });
  return snapshot;
}

function invalidateRecentSnapshot(userId: string): void {
  recentSnapshotByUser.delete(userId);
}

function notifyRecentChanged(userId: string): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(LIBRARY_RECENT_CHANGED_EVENT, { detail: { userId } })
  );
}

export function getRecentStorageKey(userId: string): string {
  return `${RECENT_STORAGE_PREFIX}${userId}`;
}

export function readRecentDocuments(userId: string | undefined): LibraryRecentEntry[] {
  if (!userId || typeof window === "undefined") return EMPTY_RECENT;
  const raw = localStorage.getItem(getRecentStorageKey(userId));
  const entries = parseRecentFromStorage(raw);
  return cacheRecentSnapshot(userId, entries);
}

export function writeRecentDocuments(
  userId: string | undefined,
  entries: LibraryRecentEntry[]
): void {
  if (!userId || typeof window === "undefined") return;
  const trimmed = entries.slice(0, LIBRARY_RECENT_MAX);
  localStorage.setItem(getRecentStorageKey(userId), JSON.stringify(trimmed));
  cacheRecentSnapshot(userId, trimmed.length === 0 ? EMPTY_RECENT : trimmed);
}

export function touchRecentDocument(
  userId: string | undefined,
  ref: LibraryDocRef
): LibraryRecentEntry[] {
  if (!userId || typeof window === "undefined") return EMPTY_RECENT;
  invalidateRecentSnapshot(userId);
  const current = readRecentDocuments(userId);
  const next: LibraryRecentEntry[] = [
    { ...ref, openedAt: Date.now() },
    ...current.filter((e) => e.id !== ref.id),
  ].slice(0, LIBRARY_RECENT_MAX);
  writeRecentDocuments(userId, next);
  notifyRecentChanged(userId);
  return readRecentDocuments(userId);
}

export function normalizeLibraryDocRefs(value: unknown): LibraryDocRef[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (e): e is LibraryDocRef =>
      e != null &&
      typeof e === "object" &&
      typeof (e as LibraryDocRef).id === "string" &&
      ((e as LibraryDocRef).itemType === "note" ||
        (e as LibraryDocRef).itemType === "reference")
  );
}

export function isSameLibraryDocRef(a: LibraryDocRef, b: LibraryDocRef): boolean {
  return a.id === b.id && a.itemType === b.itemType;
}
